/**
 * "Isto é um caminho DESTE site?" — a única resposta, para os três lugares que
 * perguntam.
 *
 * ## Por que uma função só, e por que ela mora aqui
 *
 * A mesma pergunta era feita em três lugares com três respostas diferentes: o
 * `destino` guardado até depois do login (`apps/aluno/lib/destino.ts`), o
 * `voltarPara` que o painel manda ao abrir uma sessão de suporte
 * (`routes/areas.ts`) e a conferência do mesmo `voltarPara` no navegador
 * (`apps/aluno/lib/suporte.ts`). Duas validações do mesmo perigo divergem com o
 * tempo, e a que ficar para trás é exatamente a que vaza: a auditoria nº 5 achou
 * `/\evil.com` passando nas duas cópias da regex `/^\/(?!\/)/`, enquanto a
 * terceira já o recusava.
 *
 * ## O que o navegador faz com o texto antes de ir para lá
 *
 * `window.location.href = destino` não usa a string: usa o que o **parser de
 * URL** faz dela. E o parser é mais permissivo do que qualquer regex ingênua
 * espera:
 *
 * * `\` é tratado como `/` em esquemas especiais — `/\evil.com` resolve para
 *   `https://evil.com`, e a regex que só olhava `//` deixava passar;
 * * TAB, LF e CR são **descartados de qualquer posição** antes da análise — então
 *   `/<TAB>/evil.com` vira `//evil.com`, e nenhuma comparação de prefixo o pega;
 * * espaços à frente também somem, e `javascript:` continua sendo um esquema.
 *
 * Por isso a conferência tem duas partes que não se substituem: as regras de
 * forma (determinísticas, legíveis, testáveis sem depender do parser) e a
 * confirmação **pelo próprio parser**, que é a autoridade sobre o que o navegador
 * vai fazer. Se alguma normalização futura escapar da lista de caracteres, a
 * segunda parte ainda recusa, porque ela compara a origem resolvida com a base.
 *
 * ## O que ela NÃO faz
 *
 * Não decide se o caminho existe, nem se a pessoa pode vê-lo. Ela responde uma
 * coisa: "mandar o navegador para cá o mantém neste site?".
 */

/**
 * Uma origem que não é de ninguém, só para o parser ter contra o que resolver.
 *
 * `.invalid` é reservado pelo RFC 2606 exatamente para isto: nenhum DNS a
 * resolve, então nem por acidente esta constante vira um destino real.
 */
const BASE = 'https://caminho-interno.invalid';

/**
 * Caracteres que o parser de URL apaga ou que nunca deveriam estar num caminho
 * vindo de fora: todos os controles C0 (inclui TAB, LF e CR) e o DEL.
 *
 * Escritos como escapes, e não literais: um TAB de verdade dentro de uma classe
 * de caracteres é invisível para quem lê o diff.
 */
const CONTROLES = /[\u0000-\u001f\u007f]/;

/**
 * O caminho, se for interno; `null` se não for.
 *
 * Devolve a **mesma string** que entrou, e não uma normalizada: quem chamou já
 * escreveu esse texto em algum lugar (um cookie, um corpo de requisição), e
 * devolver outra coisa faria a conferência e o uso divergirem.
 */
export function caminhoInternoSeguro(bruto: unknown): string | null {
  if (typeof bruto !== 'string') return null;

  /* Precisa começar por `/` — mata `https://host`, `javascript:`, `\\host` e o
     caminho relativo, que dependeria de onde a pessoa está. */
  if (!bruto.startsWith('/')) return null;

  /* `//host` é protocolo relativo; `/\host` é a mesma coisa para o parser. */
  if (bruto[1] === '/' || bruto[1] === '\\') return null;

  if (CONTROLES.test(bruto)) return null;

  /*
   * E a palavra final é do parser. `new URL('/\\/host', BASE).origin` devolve
   * `https://host` — é assim que o defeito foi medido, e é assim que ele fica
   * fechado mesmo que apareça uma normalização que a lista acima não previu.
   */
  try {
    if (new URL(bruto, BASE).origin !== BASE) return null;
  } catch {
    return null;
  }

  return bruto;
}

/** Só a pergunta, para quem valida antes de guardar (um `zod`, por exemplo). */
export function ehCaminhoInterno(bruto: unknown): boolean {
  return caminhoInternoSeguro(bruto) !== null;
}

/**
 * O caminho interno, ou a raiz.
 *
 * A queda para `/` é a resposta certa de quem já ia navegar: o pedido inválido
 * não vira erro na cara de quem acabou de entrar — vira a página inicial.
 */
export function caminhoInternoOuRaiz(bruto: unknown): string {
  return caminhoInternoSeguro(bruto) ?? '/';
}
